import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@client-app/i18n";
import { authService } from "@common/api/authService";
import {
  getCurrentProductCode,
  PRODUCT_CONTEXT_HEADER,
} from "@common/api/productContext";
import { useAuth } from "@/contexts/AuthContext";
import { logSentryEvent } from "@/lib/sentry";
import { parseShopifyConnectionEntry } from "@/flows/shopifyConnectionEntry";
import {
  beginOnboardingInstall,
  buildOnboardingInstallProcessingRoute,
  correlateOnboardingInstall,
  readOnboardingInstall,
} from "@/flows/shopifyOnboardingInstall";
import {
  beginPopupInstall,
  bindPopupInstallWindow,
  buildPopupInstallReturnSearch,
  clearPopupInstall,
  listActiveShopifyDomains,
  openShopifyInstallPopup,
  parsePopupInstallMessage,
  readPopupInstall,
  SHOPIFY_CONNECT_CHANNEL,
  type PopupInstallResult,
} from "@/flows/shopifyPopupInstall";
const POPUP_POLL_INTERVAL = 3000;
type PopupAttempt = {
  attemptId: string;
  workspaceId: string;
  oauthAppId: "PRIMARY" | "BACKUP";
  launchUrl: string;
};
interface Props {
  isOpen: boolean;
  platform: string | null;
  onClose: () => void;
  workspaceId?: string | null;
  scene?: "onboarding" | "admin";
  children: React.ReactNode;
  renderStatus?: (status: React.ReactNode) => React.ReactNode;
  onProgress?: (state: "checking" | "error" | "idle") => void;
}
function EntryAttempt({
  onClose,
  workspaceId,
  productCode,
  accountId,
  scene,
  onProgress,
  renderStatus = (status) => status,
  children,
}: {
  onClose: () => void;
  workspaceId: string | null;
  productCode: string;
  accountId: string;
  scene: "onboarding" | "admin";
  onProgress?: Props["onProgress"];
  renderStatus?: Props["renderStatus"];
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState<"checking" | "error" | "legacy" | "waiting">("checking");
  const [attempt, setAttempt] = useState(0);
  const [popupAttempt, setPopupAttempt] = useState<PopupAttempt | null>(null);
  const popupRef = useRef<Window | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const callbacks = useRef({ onProgress, onClose });
  callbacks.current = { onProgress, onClose };
  useEffect(() => {
    // A launched popup owns this entry; route/search changes must not restart it.
    if (popupRef.current) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    callbacks.current.onProgress?.("checking");
    logSentryEvent("shopify.connection_entry.checking", {
      has_workspace: Boolean(workspaceId),
      attempt,
      scene,
    });
    // Open only after the server picks the Shopify flow, so the manual domain
    // form never flashes an empty popup. The click's transient activation still
    // covers this lookup; a blocked popup falls back to the same-tab journey.
    let popup: Window | null = null;
    let launched = false;
    let ownedAttemptId: string | undefined;
    void (async () => {
      try {
        const response = await authService.getShopifyConnectionEntry({
          signal: controller.signal,
          headers: {
            [PRODUCT_CONTEXT_HEADER]: productCode,
            ...(workspaceId ? { "X-Workspace-Id": workspaceId } : {}),
          },
        });
        if (controller.signal.aborted) return;
        const entry = parseShopifyConnectionEntry(response);
        if (entry.mode === "manual") {
          popup?.close();
          popup = null;
          setState("legacy");
          callbacks.current.onProgress?.("idle");
          return;
        }
        const oauthAppId: "PRIMARY" | "BACKUP" =
          entry.oauthAppId ||
          (entry.appName === "Bustly AI" ? "BACKUP" : "PRIMARY");
        if (workspaceId) popup = openShopifyInstallPopup();
        if (popup && !popup.closed && workspaceId) {
          const attemptId = window.crypto.randomUUID();
          beginOnboardingInstall(
            window.sessionStorage,
            {
              accountId,
              productCode,
              workspaceId,
              scene,
              oauthAppId,
              returnPath: location.pathname,
            },
            location.search,
          );
          ownedAttemptId = attemptId;
          beginPopupInstall(window.localStorage, {
            accountId,
            productCode,
            workspaceId,
            scene,
            oauthAppId,
            attemptId,
          });
          bindPopupInstallWindow(popup.sessionStorage, attemptId);
          popup.location.href = entry.launchUrl;
          popupRef.current = popup;
          launched = true;
          setPopupAttempt({
            attemptId,
            workspaceId,
            oauthAppId,
            launchUrl: entry.launchUrl,
          });
          setState("waiting");
          logSentryEvent("shopify.connection_entry.popup", { scene });
          return;
        }
        // Popup blocked or unavailable: keep the original same-tab journey.
        popup?.close();
        popup = null;
        beginOnboardingInstall(
          window.sessionStorage,
          {
            accountId,
            productCode,
            workspaceId: workspaceId || undefined,
            scene,
            oauthAppId: entry.oauthAppId,
            returnPath: location.pathname,
          },
          location.search,
        );
        callbacks.current.onClose();
        window.location.assign(entry.launchUrl);
      } catch {
        if (!launched) popup?.close();
        if (!launched && ownedAttemptId)
          clearPopupInstall(window.localStorage, ownedAttemptId);
        if (controller.signal.aborted) return;
        setState("error");
        callbacks.current.onProgress?.("error");
        logSentryEvent(
          "shopify.connection_entry.error",
          { retryable: true, scene },
          "warn",
        );
      }
    })();
    return () => {
      controller.abort();
      if (!launched) popup?.close();
    };
  }, [
    attempt,
    productCode,
    workspaceId,
    accountId,
    scene,
    location.pathname,
    location.search,
  ]);
  useEffect(() => {
    if (!popupAttempt) return;
    const { attemptId, workspaceId: targetWorkspaceId, oauthAppId } = popupAttempt;
    const correlation = { attemptId, workspaceId: targetWorkspaceId };
    let finished = false;
    const controller = new AbortController();
    const finish = (result: PopupInstallResult) => {
      if (finished || controller.signal.aborted) return;
      finished = true;
      try {
        if (result.status === "error") throw new Error("Shopify popup failed");
        const popupIntent = readPopupInstall(window.localStorage, {
          accountId, productCode, attemptId,
        });
        if (!popupIntent || popupIntent.workspaceId !== targetWorkspaceId ||
            popupIntent.oauthAppId !== oauthAppId)
          throw new Error("Shopify popup result expired");
        const shop = result.shop || popupIntent.shop;
        const intent = readOnboardingInstall(window.sessionStorage, {
          accountId,
          productCode,
          workspaceId: targetWorkspaceId,
        });
        if (!shop || !intent) throw new Error("Shopify popup result expired");
        const correlated = correlateOnboardingInstall(
          window.sessionStorage,
          intent,
          attemptId,
          shop,
        );
        clearPopupInstall(window.localStorage, attemptId);
        logSentryEvent("shopify.connection_entry.popup_success", { scene });
        // Existing full-page return verification refreshes and resumes the original flow.
        callbacks.current.onClose();
        navigate(
          buildOnboardingInstallProcessingRoute(
            correlated,
            buildPopupInstallReturnSearch({
              workspaceId: targetWorkspaceId,
              attemptId,
              scene: correlated.scene,
            }),
          ),
          { replace: true },
        );
      } catch {
        clearPopupInstall(window.localStorage, attemptId);
        popupRef.current = null;
        setPopupAttempt(null);
        setState("error");
        callbacks.current.onProgress?.("error");
        logSentryEvent("shopify.connection_entry.popup_error", { scene }, "warn");
      }
    };
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(SHOPIFY_CONNECT_CHANNEL);
      channel.onmessage = (event) => {
        const result = parsePopupInstallMessage(event.data, correlation);
        if (result) finish(result);
      };
    } catch {}
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const result = parsePopupInstallMessage(event.data, correlation);
      if (result) finish(result);
    };
    window.addEventListener("message", onMessage);
    // Fallback when Shopify severs the popup relationship or the broadcast is missed.
    const poll = async () => {
      if (finished || controller.signal.aborted) return;
      try {
        const inventory = await authService.getWorkspaceIntegrations({
          signal: controller.signal,
          headers: {
            [PRODUCT_CONTEXT_HEADER]: productCode,
            "X-Workspace-Id": targetWorkspaceId,
          },
        });
        if (finished || controller.signal.aborted) return;
        const domains = listActiveShopifyDomains(inventory, oauthAppId);
        const intent = readPopupInstall(window.localStorage, {
          accountId, productCode, attemptId,
        });
        if (
          intent?.workspaceId === targetWorkspaceId &&
          intent.oauthAppId === oauthAppId &&
          intent.shop && domains.includes(intent.shop)
        )
          finish({ status: "success", shop: intent.shop });
      } catch {}
    };
    void poll();
    const interval = window.setInterval(() => void poll(), POPUP_POLL_INTERVAL);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener("message", onMessage);
      channel?.close();
    };
  }, [popupAttempt, accountId, productCode, scene, navigate]);
  if (state === "legacy") return <>{children}</>;
  if (state === "waiting")
    return renderStatus(
      <div className="px-5 py-3 text-sm text-[#666F8D]">
        <p role="status" aria-live="polite">
          {t("website.shopifyConnectionEntry.popupWaiting")}
        </p>
        <div className="mt-3 flex gap-4">
          <button
            type="button"
            className="underline"
            onClick={() => {
              if (!popupAttempt) return;
              try {
                if (popupRef.current && !popupRef.current.closed) {
                  popupRef.current.focus();
                  return;
                }
              } catch {}
              const reopened = openShopifyInstallPopup();
              if (reopened) {
                try {
                  bindPopupInstallWindow(reopened.sessionStorage, popupAttempt.attemptId);
                  reopened.location.href = popupAttempt.launchUrl;
                  popupRef.current = reopened;
                  reopened.focus?.();
                } catch {
                  reopened.close();
                }
              }
            }}
          >
            {t("website.shopifyConnectionEntry.popupReopen")}
          </button>
          <button
            type="button"
            className="underline"
            onClick={() => {
              try {
                popupRef.current?.close();
              } catch {}
              popupRef.current = null;
              clearPopupInstall(window.localStorage, popupAttempt?.attemptId);
              setPopupAttempt(null);
              callbacks.current.onProgress?.("idle");
              onClose();
            }}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>,
    );
  if (state === "checking") return null;
  return renderStatus(
    <div className="px-5 py-3 text-sm text-[#666F8D]">
      <p role="alert">{t("website.shopifyConnectionEntry.error")}</p>
      <div className="mt-3 flex gap-4">
        <button
          type="button"
          onClick={() => {
            setState("checking");
            setAttempt((value) => value + 1);
          }}
        >
          {t("website.shopifyConnectionEntry.retry")}
        </button>
        <button
          type="button"
          onClick={() => {
            controllerRef.current?.abort();
            callbacks.current.onProgress?.("idle");
            onClose();
          }}
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
export default function ShopifyConnectionEntry({
  isOpen,
  platform,
  onClose,
  workspaceId,
  scene = "admin",
  children,
  onProgress,
  renderStatus,
}: Props) {
  const {
    user,
    currentWorkspace,
    isLoadingProfile,
    isLoadingWorkspaces,
    isGlobalLoading,
  } = useAuth();
  const productCode = getCurrentProductCode();
  const activeWorkspaceId =
    workspaceId === undefined ? (currentWorkspace?.id ?? null) : workspaceId;
  if (!isOpen) return null;
  if (platform !== "Shopify") return <>{children}</>;
  if (!user?.id || isLoadingProfile || isLoadingWorkspaces || isGlobalLoading)
    return null;
  const contextKey = JSON.stringify([
    user.id,
    user.email ?? null,
    currentWorkspace?.id ?? null,
    activeWorkspaceId,
    productCode,
    scene,
  ]);
  return (
    <EntryAttempt
      children={children}
      key={contextKey}
      onClose={onClose}
      workspaceId={activeWorkspaceId}
      productCode={productCode}
      accountId={user.id}
      scene={scene}
      onProgress={onProgress}
      renderStatus={renderStatus}
    />
  );
}
